import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, FieldValues, DefaultValues } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useCategorizedNomenclature } from "./useCategorizedNomenclature";
import { useCategoryMenuItems } from "./useCategoryMenuItems";
import { useAccordionTabs } from "./useAccordionTabs";
import { useItemFormHandlers } from "./useItemFormHandlers";
import { useCategoryHandlers } from "./useCategoryHandlers";
import { useCategorizedSearch } from "./useCategorizedSearch";
import { IconType } from "../../icons/Icon";
import { ICategorizedNomenclatureEndpoints } from "../models/categorizedNomenclatureEndpoints";
import { useAddAdditionalLayoutButton } from "../../infrastructure/hooks/useAddAdditionalLayoutButton";
import { useFormValidationMessages } from "../../infrastructure/hooks/useFormHelper";
import { useAppContext } from "../../../AppContext";
import { usePermissions } from "../../infrastructure/hooks/usePermissions";
import {
  ICategorizableItem,
  INomenclatureCategory,
} from "../models/categorizedNomenclatureModels";
import { IReorderableItem } from "../../components/ReorderDialog";

interface UseCategorizedNomenclatureListPageProps<
  TFormModel extends FieldValues & ICategorizableItem,
  TItemModel extends ICategorizableItem,
> {
  endpoints: ICategorizedNomenclatureEndpoints;
  dialogAddTitle: string;
  dialogEditTitle: string;
  emptyListText: string;
  itemTemplate: (
    item: TItemModel | undefined,
    index: number,
    isLoading: boolean,
    actionButtons?: React.ReactNode,
    className?: string,
    onEditItem?: (item: TItemModel) => void,
  ) => JSX.Element;
  formSchema: yup.ObjectSchema<any>;
  defaultValues: () => DefaultValues<TFormModel>;
  editOnRowClick?: boolean;
  transformPayload?: (data: TFormModel) => Partial<TItemModel>;
  enableSearch?: boolean;
  uncategorizedEmoji?: string;
}

export const useCategorizedNomenclatureListPage = <
  TFormModel extends FieldValues & ICategorizableItem,
  TItemModel extends ICategorizableItem,
  TCategory extends INomenclatureCategory,
>({
  endpoints,
  dialogAddTitle,
  dialogEditTitle,
  emptyListText,
  itemTemplate,
  formSchema,
  defaultValues,
  editOnRowClick = false,
  transformPayload,
  enableSearch = false,
  uncategorizedEmoji,
}: UseCategorizedNomenclatureListPageProps<TFormModel, TItemModel>) => {
  const { selectedStudioId } = useAppContext();
  const { userHavePermissions } = usePermissions();
  const [activeAccordionIndexes, setActiveAccordionIndexes] = useState<
    number[]
  >([0]);

  // Build keys to invalidate on mutations
  const keysToInvalidate = useMemo(() => {
    const keys = [
      [endpoints.getAllCategorized, selectedStudioId],
      [endpoints.getAll, selectedStudioId],
      [endpoints.getAllWithDeleted, selectedStudioId],
      [endpoints.getById, selectedStudioId],
      [endpoints.getAllCategories, selectedStudioId],
    ];
    if (endpoints.getNomenclatureAdditionalData) {
      keys.push([endpoints.getNomenclatureAdditionalData]);
    }
    return keys;
  }, [endpoints, selectedStudioId]);

  // Main data hook
  const {
    categories,
    items,
    itemsByCategory,
    isLoading,
    isSubmitting,
    itemDialogOpen,
    editingItemId,
    setItemDialogOpen,
    setEditingItemId,
    categoryDialogOpen,
    editingCategoryId,
    setCategoryDialogOpen,
    setEditingCategoryId,
    reorderItemsDialogOpen,
    reorderCategoriesDialogOpen,
    reorderingCategoryId,
    setReorderItemsDialogOpen,
    setReorderCategoriesDialogOpen,
    openReorderItemsDialog,
    getItemsForReorder,
    createItem,
    updateItem,
    reorderItems,
    createCategory,
    updateCategory,
    reorderCategories,
    toggleCategoryDeletionStatus,
  } = useCategorizedNomenclature<TItemModel, TCategory>(
    endpoints,
    keysToInvalidate,
  );

  // Search hook
  const {
    isSearching,
    onSearch,
    filteredCategories: searchFilteredCategories,
    filteredItemsByCategory: searchFilteredItemsByCategory,
    searchExpandedIndexes,
  } = useCategorizedSearch<TItemModel, TCategory>(
    categories,
    itemsByCategory,
    enableSearch,
  );

  // Use filtered data when searching, original data otherwise
  const effectiveCategories = isSearching
    ? searchFilteredCategories
    : categories;
  const effectiveItemsByCategory = isSearching
    ? searchFilteredItemsByCategory
    : itemsByCategory;

  // Auto-expand all matching categories when searching
  const [savedAccordionIndexes, setSavedAccordionIndexes] = useState<
    number[] | null
  >(null);

  useEffect(() => {
    if (isSearching && searchExpandedIndexes) {
      if (savedAccordionIndexes === null) {
        setSavedAccordionIndexes(activeAccordionIndexes);
      }
      setActiveAccordionIndexes(searchExpandedIndexes);
    } else if (!isSearching && savedAccordionIndexes !== null) {
      setActiveAccordionIndexes(savedAccordionIndexes);
      setSavedAccordionIndexes(null);
    }
  }, [isSearching, searchExpandedIndexes]);

  // Item form setup
  const itemForm = useForm<TFormModel>({
    resolver: yupResolver(formSchema),
    defaultValues: defaultValues(),
    mode: "onSubmit",
  });
  useFormValidationMessages<TFormModel>(itemForm);

  // Item form handlers
  const {
    onEditItemClick,
    onHideItemDialog,
    onSubmitItem,
    handleItemRowClick,
  } = useItemFormHandlers<TFormModel, TItemModel>({
    form: itemForm,
    defaultValues,
    setItemDialogOpen,
    setEditingItemId,
    createItem,
    updateItem,
    transformPayload,
    editOnRowClick,
    userHavePermissions,
    updateEndpoint: endpoints.update,
  });

  // Category handlers
  const {
    handleAddCategoryClick,
    onEditCategoryClick,
    onHideCategoryDialog,
    onSubmitCategory,
  } = useCategoryHandlers<TCategory>({
    setCategoryDialogOpen,
    setEditingCategoryId,
    createCategory,
    updateCategory,
  });

  // Add category button (top-right + button)
  useAddAdditionalLayoutButton({
    iconType: IconType.PlusIcon,
    buttonOnClick: handleAddCategoryClick,
    rawPath: endpoints.createCategory,
  });

  // Category menu items
  const { getCategoryMenuItems, getUncategorizedMenuItems } =
    useCategoryMenuItems({
      endpoints,
      onAddItem: (categoryId) => onEditItemClick(undefined, categoryId),
      onEditCategory: onEditCategoryClick,
      onReorderItems: openReorderItemsDialog,
      onDeleteCategory: toggleCategoryDeletionStatus,
    });

  // Accordion tabs
  const { accordionTabs, activeCategories } = useAccordionTabs<
    TItemModel,
    TCategory
  >({
    categories: effectiveCategories as TCategory[],
    itemsByCategory: effectiveItemsByCategory,
    isLoading,
    emptyListText,
    itemTemplate,
    onItemClick: handleItemRowClick,
    onEditItem: (item) => onEditItemClick(item.id, item.categoryId),
    getCategoryMenuItems,
    getUncategorizedMenuItems,
    toggleDeletionEndpoint: endpoints.toggleDeletionStatus,
    updateEndpoint: endpoints.update,
    invalidateKeys: keysToInvalidate,
    isSearching,
    uncategorizedEmoji,
  });

  // Reorder handlers
  const handleReorderItems = useCallback(
    async (orderedIds: number[]) => {
      await reorderItems(reorderingCategoryId ?? null, orderedIds);
    },
    [reorderItems, reorderingCategoryId],
  );

  const handleReorderCategories = useCallback(
    async (orderedIds: number[]) => {
      await reorderCategories(orderedIds);
    },
    [reorderCategories],
  );

  // Computed values
  const itemDialogTitle = useMemo(
    () => (editingItemId ? dialogEditTitle : dialogAddTitle),
    [editingItemId, dialogAddTitle, dialogEditTitle],
  );

  const itemsForReorder: IReorderableItem[] = useMemo(() => {
    return getItemsForReorder(reorderingCategoryId).map((item) => ({
      id: item.id!,
      name: item.name,
    }));
  }, [getItemsForReorder, reorderingCategoryId]);

  const categoriesForReorder: IReorderableItem[] = useMemo(() => {
    return activeCategories.map((cat) => ({
      id: cat.id,
      name: cat.name,
    }));
  }, [activeCategories]);

  const showReorderButton =
    activeCategories.length > 1 &&
    userHavePermissions(endpoints.reorderCategories);

  return {
    // Data
    items,
    categories,
    isLoading,
    isSubmitting,
    accordionTabs,
    activeAccordionIndexes,
    setActiveAccordionIndexes,

    // Search
    isSearching,
    onSearch,
    enableSearch,

    // Item form
    itemForm,
    itemDialogOpen,
    editingItemId,
    itemDialogTitle,
    onHideItemDialog,
    onSubmitItem,

    // Category form
    categoryDialogOpen,
    editingCategoryId,
    onHideCategoryDialog,
    onSubmitCategory,

    // Reorder
    showReorderButton,
    reorderItemsDialogOpen,
    reorderCategoriesDialogOpen,
    setReorderItemsDialogOpen,
    setReorderCategoriesDialogOpen,
    itemsForReorder,
    categoriesForReorder,
    handleReorderItems,
    handleReorderCategories,
  };
};
